import { useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { Market } from '../dataProvider/types';
import { SanctionsRow } from './SanctionsRow';
import { useSanctionsQueue } from './useSanctionsQueue';
import type { SanctionsRow as Row } from './sanctionsQueueModel';

/**
 * The sanctions-screening queue (D-sanctions, Phase 7 surface 3). A review worklist over the
 * engine-written `sanctionsMatches` collection: members screened against the sanctions lists
 * (OFAC, UK HMT, PEP), potential matches surfaced for compliance review. The matches are
 * ENGINE-WRITTEN stand-ins; there is NO admin run-a-screen action (screening is the engine's job).
 * The admin reviews each match, clearing a false positive or confirming a true match, both
 * recorded determinations through the chokepoint with a mandatory reason.
 *
 * Confirming records a sanctions status; it does NOT flip fraudStatus and the downstream
 * enforcement (login block, payout hold) is a cross-repo seam. A member's sanctions standing is
 * DERIVED (an active confirmed match flags them), never a stored duplicate flag.
 *
 * OVERSIGHT-GLOBAL with a region FACET: sanctions is cross-border, so the queue spans markets by
 * default (the audit-log / DSAR posture, NOT the operational switcher); the facet narrows by the
 * match's market by value. ROLE-gated to compliance and super_admin.
 */

const Section = ({
  title,
  rows,
  emptyText,
  testId,
  now,
}: {
  title: string;
  rows: Row[];
  emptyText: string;
  testId: string;
  now: number;
}) => (
  <Box sx={{ mb: 4 }} data-testid={testId}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title} ({rows.length})
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    ) : (
      rows.map((row) => <SanctionsRow key={row.id} row={row} now={now} />)
    )}
  </Box>
);

/** The surface without the access wrapper. Exported so tests can inject `asOf` (the clock the
 *  review writes stamp their records with). */
export const SanctionsQueueView = ({ asOf }: { asOf?: number }) => {
  const [now] = useState<number>(() => asOf ?? Date.now());
  const [regionFilter, setRegionFilter] = useState<Market | null>(null);
  const { view, isLoading } = useSanctionsQueue(regionFilter);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Compliance
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Sanctions screening
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Potential sanctions matches the screening engine produced, for review. A
        match is a name the member resembles on a list, not a proven hit; most
        are false positives. Clearing or confirming a match is a recorded
        determination with a reason. Confirming records the determination only;
        the block and any payout hold are enforced downstream. The view spans
        both markets by default.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="sanctions-region-label">Region</InputLabel>
          <Select
            labelId="sanctions-region-label"
            label="Region"
            value={regionFilter ?? 'all'}
            inputProps={{ 'data-testid': 'sanctions-region' }}
            onChange={(e) =>
              setRegionFilter(
                e.target.value === 'all' ? null : (e.target.value as Market),
              )
            }
          >
            <MenuItem value="all">All markets</MenuItem>
            <MenuItem value="uk">UK</MenuItem>
            <MenuItem value="us">US</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {isLoading || !view ? (
        <Loading />
      ) : (
        <>
          <Section
            title="Pending review"
            rows={view.pending}
            emptyText="No matches awaiting review in scope."
            testId="sanctions-section-pending"
            now={now}
          />
          <Divider sx={{ mb: 3 }} />
          <Section
            title="Reviewed"
            rows={view.resolved}
            emptyText="No reviewed matches in scope."
            testId="sanctions-section-resolved"
            now={now}
          />
        </>
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /sanctions. ROLE-gated to compliance and super_admin: a viewer's
 * read-everywhere '*:read' wildcard would slip through a capability read-gate, so the explicit role
 * gate is what holds the boundary (D-sanctions).
 */
export const SanctionsQueue = () => (
  <RequireAccess roles={['compliance', 'super_admin']}>
    <SanctionsQueueView />
  </RequireAccess>
);
